import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { XIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';

enum BadgeVariant {
  DEFAULT = 'default',
  SECONDARY = 'secondary',
  DESTRUCTIVE = 'destructive',
  SUCCESS = 'success',
  WARNING = 'warning',
  INFO = 'info',
  OUTLINE = 'outline',
  GHOST = 'ghost',
  LINK = 'link',
}

const badgeBase = [
  'group/badge inline-flex w-fit shrink-0 items-center justify-center gap-1',
  'overflow-hidden rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap',
  'transition-all',
  'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
  'has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
  'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
  '[&>svg]:pointer-events-none [&>svg]:size-3!',
].join(' ');

const badgeVariants = cva(badgeBase, {
  variants: {
    variant: {
      [BadgeVariant.DEFAULT]: 'bg-primary text-primary-foreground [a]:hover:bg-primary/80',
      [BadgeVariant.SECONDARY]: 'bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80',
      [BadgeVariant.DESTRUCTIVE]: [
        'bg-destructive/10 text-destructive [a]:hover:bg-destructive/20',
        'focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40',
      ].join(' '),
      [BadgeVariant.SUCCESS]: 'bg-gain-soft text-gain',
      [BadgeVariant.WARNING]: 'bg-warn-soft text-warn-deep',
      [BadgeVariant.INFO]: 'bg-info-soft text-info-deep',
      [BadgeVariant.OUTLINE]: 'border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground',
      [BadgeVariant.GHOST]: 'hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50',
      [BadgeVariant.LINK]: 'text-primary underline-offset-4 hover:underline',
    },
    size: {
      default: 'h-6',
      sm: 'h-5 px-1.5 text-[10px]',
    },
  },
  defaultVariants: {
    variant: BadgeVariant.DEFAULT,
    size: 'default',
  },
});

interface BadgeExtraProps {
  variant?: BadgeVariant;
  color?: string;
  icon?: React.ReactNode;
  onDelete?: () => void;
}

function Badge({
  className,
  variant = BadgeVariant.DEFAULT,
  size,
  color,
  icon,
  onDelete,
  children,
  render,
  ...props
}: useRender.ComponentProps<'span'> & Omit<VariantProps<typeof badgeVariants>, 'variant'> & BadgeExtraProps) {
  const colorStyle: React.CSSProperties | undefined = color
    ? {
        color,
        backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)`,
        border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`,
      }
    : undefined;

  const content =
    icon || onDelete ? (
      <>
        {icon && <span className="shrink-0 [&_svg]:size-3">{icon}</span>}
        <span>{children}</span>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            aria-label="Remove"
            className="shrink-0 rounded-full p-0.5 opacity-60 hover:opacity-100 focus-visible:outline-none"
          >
            <XIcon className="size-3 fill-current" />
          </button>
        )}
      </>
    ) : (
      children
    );

  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant, size }), className),
        style: colorStyle,
        children: content,
      },
      props
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  });
}

export { Badge, BadgeVariant, badgeVariants };
